import {
  hexToBytes,
  type SignedBundleManifest,
  verifyBundleFile,
  verifyBundleSignature,
} from '@runtime/bundle-manifest.js';
import { loadConfigFromString, loadLicenseFromString, type LoadResult } from '@runtime/loader.js';
import type { KeyValueStore } from '@runtime/storage.js';
import { verifyRevocationList } from '@license/revocation.js';
import type { License as LicenseType } from '@license/types.js';
import type { MobiLauncherConfig } from '@schema/index.js';

/**
 * Public (verify-only) half of the dev keypair. Demo/marketing builds use
 * it to verify demo bundles; a customer launcher template must NOT ship with
 * it — see {@link resolveBundlePublicKey}.
 */
const DEV_BUNDLE_PUBLIC_KEY_HEX =
  '696796879b51f884dbe833c80742f9cc6365b3c4070da14c8d4297007e610b12';

/**
 * Ed25519 public key used to verify the signed bundle manifest. Baked at
 * build time; falls back to the dev keypair's public half so demo/dev
 * bundles verify without extra config. Production sets the env var.
 *
 * Guard: the offline launcher template (built with `--mode bundle`) is what
 * paying customers deploy. Shipping it with the dev key means real,
 * production-signed manifests won't verify — so when the env var is missing
 * in a bundle build we surface a loud error rather than silently baking the
 * dev key. Set `VITE_LICENSE_PUBLIC_KEY_HEX` to the production public key
 * (it must match the licensing-server's `LICENSE_PRIVATE_KEY_HEX`).
 */
function resolveBundlePublicKey(): string {
  const fromEnv = import.meta.env['VITE_LICENSE_PUBLIC_KEY_HEX'] as string | undefined;
  if (fromEnv !== undefined && fromEnv.length > 0) return fromEnv;
  // Hosted PROD build with no key configured: do NOT fall back to the
  // publicly-committed dev key — that would let a dev-key-signed license
  // verify in production. Return empty so license + bundle verification fail
  // CLOSED to Core (no paid features), never open to a forgeable key.
  if (import.meta.env.PROD && import.meta.env.MODE !== 'bundle') {
    console.error(
      '[MobiLauncher] VITE_LICENSE_PUBLIC_KEY_HEX is unset in a PROD build — ' +
        'license/bundle verification disabled; running unlicensed (Core). ' +
        'Set it to the production public key.',
    );
    return '';
  }
  if (import.meta.env.MODE === 'bundle') {
    console.error(
      '[MobiLauncher] VITE_LICENSE_PUBLIC_KEY_HEX is unset in a launcher-template ' +
        '(--mode bundle) build — falling back to the DEV key. Production-signed ' +
        'manifests will fail to verify. Set it to the production public key.',
    );
  }
  return DEV_BUNDLE_PUBLIC_KEY_HEX;
}

const BUNDLE_PUBLIC_KEY_HEX = resolveBundlePublicKey();

/**
 * Pinned keyring for license/revocation-list verification (key rotation). A
 * license tagged with a `keyId` verifies against `keyring[keyId]`; an untagged
 * license uses {@link BUNDLE_PUBLIC_KEY_HEX} (the default). Populate by baking
 * `VITE_LICENSE_PUBLIC_KEYS` as a JSON map `{ "<keyId>": "<hex>" }` — ship the
 * NEXT key here before the server starts signing with it, so devices already
 * trust it when rotation happens. Malformed entries are skipped, never fatal.
 */
function resolveLicenseKeyring(): Record<string, Uint8Array> {
  const raw = import.meta.env['VITE_LICENSE_PUBLIC_KEYS'] as string | undefined;
  if (!raw) return {};
  const out: Record<string, Uint8Array> = {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const [keyId, hex] of Object.entries(parsed)) {
      if (typeof hex !== 'string') continue;
      try {
        out[keyId] = hexToBytes(hex);
      } catch {
        // Skip a malformed key rather than failing the whole keyring.
      }
    }
  } catch {
    // Malformed JSON — no keyring (untagged licenses still verify).
  }
  return out;
}

const LICENSE_KEYRING = resolveLicenseKeyring();

/**
 * Strict signing. When baked true (`VITE_REQUIRE_SIGNED_BUNDLE=true`), the
 * launcher refuses to boot unless a valid signed manifest is present —
 * closing the "attacker deletes manifest.json to downgrade to unsigned"
 * gap. The flag lives in the BUILD (the trusted template JS), never in the
 * deletable bundle, so it can't be stripped alongside the manifest.
 * Default off, so hosted / demo / unsigned bundles boot normally.
 */
const REQUIRE_SIGNED_BUNDLE =
  (import.meta.env['VITE_REQUIRE_SIGNED_BUNDLE'] as string | undefined) === 'true';

export type BundleVerifyResult =
  | { kind: 'absent' } // no manifest.json → not a signed bundle; boot normally
  | { kind: 'ok' }
  | { kind: 'failed'; message: string };

/** Replace the last path segment of `url` (sans query) with `name`. */
function resolveSibling(url: string, name: string): string {
  const base = url.split('?')[0] ?? url;
  const slash = base.lastIndexOf('/');
  return slash >= 0 ? base.slice(0, slash + 1) + name : name;
}

/**
 * Resolve where the signed manifest + its covered files live for a given
 * config URL, and whether this is the HOSTED path.
 *
 *  - HOSTED (`…/configs/<tenant>`): manifest at `…/bundle/<tenant>/manifest`;
 *    `config.json` maps to the config endpoint itself, `license.json` to
 *    `…/licenses/by-tenant/<tenant>`. (Before, the boot looked for a sibling
 *    `…/configs/manifest.json` that never exists → a 404 on every boot.)
 *  - BUNDLED (offline ZIP, e.g. `./config.json`): manifest + files are
 *    siblings of the config, as shipped in the ZIP.
 */
function resolveBundleUrls(configUrl: string): {
  manifestUrl: string;
  fileUrl: (path: string) => string;
  hosted: boolean;
} {
  // Defensive: trim so a stray newline/space in the config URL can't leak into
  // an encoded tenant id (…/bundle/demo%0A/manifest).
  const trimmed = configUrl.trim();
  const clean = trimmed.split('?')[0] ?? trimmed;
  const m = /^(.*)\/configs\/([^/]+)$/.exec(clean);
  if (m) {
    const base = m[1] ?? '';
    const tenant = (m[2] ?? '').trim();
    return {
      hosted: true,
      manifestUrl: `${base}/bundle/${encodeURIComponent(tenant)}/manifest`,
      fileUrl: (path) =>
        path === 'license.json'
          ? `${base}/licenses/by-tenant/${encodeURIComponent(tenant)}`
          : trimmed,
    };
  }
  return {
    hosted: false,
    manifestUrl: resolveSibling(trimmed, 'manifest.json'),
    fileUrl: (path) => resolveSibling(trimmed, path),
  };
}

/**
 * Verify a signed bundle's integrity before the launcher trusts it.
 *
 * Fetches the signed manifest for the deployment (see {@link resolveBundleUrls}
 * for hosted vs bundled), checks its Ed25519 signature against the embedded
 * public key, and re-fetches + re-hashes every covered file.
 *
 * No usable manifest (404 / unsigned / unreachable) → `absent` (boot normally),
 * unless strict signing is on. On a real mismatch the behaviour depends on the
 * deployment:
 *  - BUNDLED (offline, file-synced ZIP): a mismatch is the tamper case the
 *    signing exists for → hard `failed` (the device refuses to boot).
 *  - HOSTED (config from our authenticated HTTPS api): a mismatch is almost
 *    always a config republished mid-boot — a benign race, not tampering
 *    (which TLS already prevents). We log it and boot rather than brick a
 *    device fleet-wide over a sub-second race.
 *
 * `opts.requireSigned` / `opts.publicKeyHex` override the baked defaults —
 * primarily for tests (and future key rotation).
 */
export async function verifyBundleIntegrity(
  configUrl: string,
  opts: { requireSigned?: boolean; publicKeyHex?: string } = {},
): Promise<BundleVerifyResult> {
  const requireSigned = opts.requireSigned ?? REQUIRE_SIGNED_BUNDLE;
  const publicKeyHex = opts.publicKeyHex ?? BUNDLE_PUBLIC_KEY_HEX;
  // "No usable signed manifest" — absent normally, hard-fail under strict.
  const absent = (): BundleVerifyResult =>
    requireSigned
      ? {
          kind: 'failed',
          message: 'this device requires a signed bundle, but no valid signed manifest was found',
        }
      : { kind: 'absent' };

  const { manifestUrl, fileUrl, hosted } = resolveBundleUrls(configUrl);
  // Hosted mismatches log + boot; bundled mismatches fail closed (see JSDoc).
  const onMismatch = (message: string): BundleVerifyResult => {
    if (hosted) {
      console.error(`[MobiLauncher] hosted bundle integrity check failed (booting anyway): ${message}`);
      return absent();
    }
    return { kind: 'failed', message };
  };

  let manifest: SignedBundleManifest;
  try {
    const res = await fetch(manifestUrl, { cache: 'no-store' });
    // Any non-2xx (404 missing, 5xx transient) → no usable manifest.
    if (!res.ok) return absent();
    manifest = (await res.json()) as SignedBundleManifest;
  } catch {
    // No reachable manifest (offline / parse error on a non-JSON 200).
    return absent();
  }

  // A 200 that isn't a structurally-valid signed manifest (e.g. an SPA
  // host serving index.html for unknown paths, or a stray file) means
  // "no signed bundle here". A genuinely tampered bundle keeps a
  // well-formed manifest but fails the signature / hash checks below.
  if (typeof manifest.signature !== 'string' || !Array.isArray(manifest.files)) {
    return absent();
  }
  if (!(await verifyBundleSignature(manifest, hexToBytes(publicKeyHex)))) {
    return onMismatch('bundle manifest signature is invalid');
  }

  for (const file of manifest.files) {
    let content: string;
    try {
      const res = await fetch(fileUrl(file.path), { cache: 'no-store' });
      if (!res.ok) {
        return onMismatch(`covered file ${file.path} is missing (HTTP ${String(res.status)})`);
      }
      content = await res.text();
    } catch {
      return onMismatch(`could not read covered file ${file.path}`);
    }
    if (!(await verifyBundleFile(manifest, file.path, content))) {
      return onMismatch(`covered file ${file.path} failed its integrity check`);
    }
  }

  return { kind: 'ok' };
}

/**
 * Fetch the config from `url` (production: a relative path inside the
 * MobiControl File Sync directory; demo: served by Vite from `/public`)
 * and validate it through the schema.
 */
export async function fetchAndLoadConfig(url: string): Promise<LoadResult<MobiLauncherConfig>> {
  let raw: string;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      return {
        ok: false,
        failure: {
          kind: 'httpError',
          status: res.status,
          message: `config fetch failed: HTTP ${String(res.status)}`,
        },
      };
    }
    raw = await res.text();
  } catch (err) {
    return {
      ok: false,
      failure: {
        kind: 'network',
        message: err instanceof Error ? err.message : String(err),
        // navigator.onLine is reliable on Android WebView + modern browsers
        // for the "no network at all" case (which is the dominant offline
        // failure mode on a kiosk). DNS / CORS / TLS failures may report
        // `true` here — that's fine, the screen still says "we can't
        // reach the server" either way.
        offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      },
    };
  }
  return loadConfigFromString(raw);
}

const LKG_CONFIG_PREFIX = 'mobilauncher.lkg.config:';
const LKG_LICENSE_PREFIX = 'mobilauncher.lkg.license:';
const LKG_REVOCATION_PREFIX = 'mobilauncher.lkg.revocation:';

/**
 * Fetch + verify + cache the signed revocation list for a tenant, returning the
 * set of revoked license ids. Opportunistic and FAIL-OPEN: on a fetch/verify
 * failure we fall back to the last-known-good cached list, and if there's no
 * cache either we return an empty set (don't revoke) — availability over
 * strictness, matching the offline-first design. Only signature-verified lists
 * are ever cached, so a tampered/forged list can't poison the cache.
 */
export async function fetchRevocationList(
  url: string,
  expectedTenantId: string,
  store: KeyValueStore,
): Promise<ReadonlySet<string>> {
  const cacheKey = LKG_REVOCATION_PREFIX + expectedTenantId;
  const publicKey = (() => {
    try {
      return BUNDLE_PUBLIC_KEY_HEX.length > 0 ? hexToBytes(BUNDLE_PUBLIC_KEY_HEX) : null;
    } catch {
      return null;
    }
  })();
  if (publicKey === null) return new Set();

  const fromCache = (): ReadonlySet<string> => {
    const cached = store.get(cacheKey);
    if (cached === null) return new Set();
    try {
      const ids = JSON.parse(cached) as unknown;
      return Array.isArray(ids) ? new Set(ids.filter((x): x is string => typeof x === 'string')) : new Set();
    } catch {
      return new Set();
    }
  };

  let list: unknown;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return fromCache(); // transient / 404 → keep any cached list
    list = await res.json();
  } catch {
    return fromCache(); // offline → cached list
  }

  const verified = await verifyRevocationList({
    publicKey,
    keyring: LICENSE_KEYRING,
    list,
    expectedTenantId,
  });
  if (verified === null) return fromCache(); // bad signature/tenant → don't trust it
  store.set(cacheKey, JSON.stringify(verified.revokedIds));
  return new Set(verified.revokedIds);
}
const CLOCK_HWM_KEY = 'mobilauncher.clock.hwm';

/**
 * Advance and return the monotonic clock high-water-mark (Unix seconds): the
 * max wall-clock this device has ever observed, persisted across boots. Used
 * as the anti-rollback floor for license expiry — winding the device clock
 * back can't lower it, so an expired license can't be un-expired by resetting
 * the clock (kiosks are physically accessible and often lack NTP). Called once
 * per boot before license verification.
 */
export function advanceClockFloor(store: KeyValueStore, nowSec: number): number {
  const raw = store.get(CLOCK_HWM_KEY);
  const stored = raw !== null ? Number.parseInt(raw, 10) : 0;
  const floor = Math.max(Number.isFinite(stored) ? stored : 0, nowSec);
  store.set(CLOCK_HWM_KEY, String(floor));
  return floor;
}

/**
 * Fetch + validate the config, writing it to the store as last-known-good on
 * success. When the LIVE fetch fails transiently (server unreachable: network
 * or httpError) we fall back to the cached LKG so a kiosk rebooting out of
 * network coverage still comes up usable instead of stranding on the error
 * screen. A schema/parse/tamper failure is NOT masked by the cache — a broken
 * live config must still surface. `fromCache` lets the caller flag offline use.
 */
export async function fetchConfigWithCache(
  url: string,
  store: KeyValueStore,
): Promise<{ result: LoadResult<MobiLauncherConfig>; fromCache: boolean }> {
  const live = await fetchAndLoadConfig(url);
  if (live.ok) {
    store.set(LKG_CONFIG_PREFIX + url, JSON.stringify(live.value));
    return { result: live, fromCache: false };
  }
  if (live.failure.kind === 'network' || live.failure.kind === 'httpError') {
    const cached = store.get(LKG_CONFIG_PREFIX + url);
    if (cached !== null) {
      const parsed = loadConfigFromString(cached);
      if (parsed.ok) return { result: parsed, fromCache: true };
    }
  }
  return { result: live, fromCache: false };
}

/**
 * Outcome of a live license fetch:
 *  - `ok`        — the server returned a license body.
 *  - `notFound`  — the server definitively has no license for this device
 *                  (HTTP 404/410). This is how the licensing server signals a
 *                  revoked/lapsed subscription; it must demote the device, NOT
 *                  be masked by a cached license.
 *  - `transient` — network error or a 5xx/other non-OK status. The server's
 *                  intent is unknown, so a cached license may be reused to keep
 *                  the kiosk on its tier through an outage.
 */
export type LicenseFetchResult =
  | { kind: 'ok'; text: string }
  | { kind: 'notFound' }
  | { kind: 'transient' };

/**
 * Fetch the raw license text. The returned text is verified separately by
 * {@link verifyLicenseText} once the tenant the config resolved to is known (so
 * we can bind-check the license to it). A 404/410 is reported as `notFound`
 * (definitive denial) and distinguished from `transient` failures so revocation
 * can propagate — see {@link fetchLicenseTextWithCache}.
 */
export async function fetchLicenseText(url: string): Promise<LicenseFetchResult> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (res.status === 404 || res.status === 410) return { kind: 'notFound' };
    if (!res.ok) return { kind: 'transient' };
    return { kind: 'ok', text: await res.text() };
  } catch {
    return { kind: 'transient' };
  }
}

/**
 * Like {@link fetchLicenseText} but persists the last good license text and
 * falls back to it ONLY on a transient failure — so an offline boot keeps the
 * device's tier instead of silently dropping to Core. On a definitive
 * `notFound` (revoked/lapsed license) the cached entry is deleted and the
 * device drops to Core, so a canceled subscription actually takes effect at the
 * next boot rather than living forever on the cached blob. Safe either way: the
 * cached text is still signature/expiry-verified on read by
 * {@link verifyLicenseText}, so an expired or forged cached license also yields
 * Core.
 */
export async function fetchLicenseTextWithCache(
  url: string,
  store: KeyValueStore,
): Promise<string | null> {
  const live = await fetchLicenseText(url);
  const key = LKG_LICENSE_PREFIX + url;
  if (live.kind === 'ok') {
    store.set(key, live.text);
    return live.text;
  }
  if (live.kind === 'notFound') {
    store.remove(key);
    return null;
  }
  return store.get(key);
}

/**
 * Verify a fetched license against the build's public key AND the tenant the
 * config resolved to, returning the License or `null` (→ run as Core). This is
 * the REAL on-device path: the Ed25519 signature, the tenant binding, and the
 * expiry are all checked (via the runtime's `loadLicenseFromString`). Any
 * failure — no text, unconfigured/empty public key, bad signature, wrong
 * tenant, or expired — falls closed to Core, never silently grants features.
 */
/**
 * Best-effort read of the `id` field from raw license text — used to report
 * WHICH license a device is on even when verification fails (a revoked license
 * returns null from {@link verifyLicenseText}, but the admin still wants its
 * id). Untrusted: the id is only a fleet-view label, never a trust decision.
 * Returns null on absent/malformed text or a non-string id.
 */
export function licenseIdFromText(raw: string | null): string | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown };
    return typeof parsed.id === 'string' ? parsed.id : null;
  } catch {
    return null;
  }
}

export async function verifyLicenseText(
  raw: string | null,
  expectedTenantId: string,
  /** Anti-rollback clock floor from {@link advanceClockFloor} (Unix seconds). */
  clockFloor?: number,
  /** Revoked license ids from {@link fetchRevocationList}. */
  revokedIds?: ReadonlySet<string>,
): Promise<LicenseType | null> {
  if (raw === null || BUNDLE_PUBLIC_KEY_HEX.length === 0) return null;
  let publicKey: Uint8Array;
  try {
    publicKey = hexToBytes(BUNDLE_PUBLIC_KEY_HEX);
  } catch {
    return null;
  }
  return loadLicenseFromString({
    raw,
    publicKey,
    keyring: LICENSE_KEYRING,
    expectedTenantId,
    ...(clockFloor !== undefined ? { clockFloor } : {}),
    ...(revokedIds !== undefined ? { revokedIds } : {}),
  });
}
